import { useState, useEffect } from 'react'
// import { useTheme } from '@material-ui/core'
import useSettings from 'src/hooks/useSettings'
import useAuth from 'src/hooks/useAuth'
import { AnimatePresence } from 'framer-motion'
import useChat from 'src/components/chatbot/hooks/useChat'
import { useNavbarContext } from 'src/contexts/NavbarContext'
import commonService from 'src/components/chatbot/services/commonService'
import ChatbotWidgetButton from 'src/components/chatbot/components/ChatbotWidgetButton'
import ChatInterface from 'src/components/chatbot/components/ChatInterface'
import FilterWidget from 'src/components/chatbot/components/FilterWidget'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
// TODO list:
// Report handling
// Chat thread list -> Chat history when user clicks on a thread

export default function ChatbotMainWidget() {
  const { isAuthenticated } = useAuth()
  const { showChat, setShowChat, scrollToBottom, getCachedData, updateCache, setDiseaseMappingFromSelections } =
    useChatbotStore()
  const [showHPOWidget, setShowHPOWidget] = useState(false)
  const [showDiseaseWidget, setShowDiseaseWidget] = useState(false)
  const [showSamplesWidget, setShowSamplesWidget] = useState(false)
  const [showPanelsWidget, setShowPanelsWidget] = useState(false)
  const [hpoTerms, setHpoTerms] = useState([])
  const [diseases, setDiseases] = useState([])
  const [selectedSamples, setSelectedSamples] = useState([])
  const [selectedPanels, setSelectedPanels] = useState([])
  const { fileId, sampleName } = useNavbarContext()
  const { clearChatState, setVisibility } = useChat()
  const { themeMode } = useSettings()
  const isDarkMode = themeMode === 'dark'
  // const theme = useTheme()
  // const [buttonPosition, setButtonPosition] = useState(null)

  // Suggested queries to show when the user is on the variants table
  const suggestedQueries = [
    'Show me variants with ACMG classification of pathogenic or likely pathogenic associated with patients phenotype',
    'Filter for rare variants (MAF < 1%) in genes known to be associated with patients phenotype with a high predicted impact (e.g., CADD score > 20).',
    'Show me de novo variants classified as pathogenic or likely pathogenic (ACMG).',
    'Filter for homozygous or compound heterozygous variants in genes known to be associated with this patients phenotypes',
    "Show me variants in ClinVar with a 'pathogenic' or 'likely pathogenic' assertion that match the patient's phenotypes.",
    'Filter variants by phenotype association and show only those with high pathogenicity scores',
    "Provide a summary of rare variants (MAF < 1%) that match the patient's clinical symptoms.",
    'Highlight any novel or rare variants (not in ClinVar) with potential disease associations based on literature.',
  ]

  // Check if user is on variants table page
  const isVariantsTablePage = !!fileId && !!sampleName

  // We need to populate the cache with data when the user is authenticated
  // TODO: Move try-catch blocks into commonService?
  useEffect(() => {
    const initializeCache = async () => {
      const contextFunctions = { getCachedData, updateCache }

      if (!getCachedData('sample')) {
        try {
          await commonService.fetchAllSamples(contextFunctions)
        } catch (error) {
          console.error('Error fetching samples:', error)
        }
      }

      // UNUSED
      // if (!getCachedData('disease')) {
      //   try {
      //     await commonService.fetchAllDiseases(contextFunctions)
      //   } catch (error) {
      //     console.error('Error fetching diseases:', error)
      //   }
      // }

      if (!getCachedData('hpo')) {
        try {
          await commonService.fetchHPOTerms(contextFunctions)
        } catch (error) {
          console.error('Error fetching HPO terms:', error)
        }
      }

      if (!getCachedData('panel')) {
        try {
          await commonService.fetchPanels(contextFunctions)
        } catch (error) {
          console.error('Error fetching panels:', error)
        }
      }
    }

    if (isAuthenticated) {
      initializeCache()
    }
  }, [isAuthenticated, getCachedData, updateCache])

  // Keep dedicated disease mapping in sync with current selections
  useEffect(() => {
    try {
      setDiseaseMappingFromSelections(diseases)
    } catch (e) {
      console.log('Error syncing disease mapping from selections', e, diseases)
    }
  }, [diseases, setDiseaseMappingFromSelections])

  // Expose setter functions to window object
  useEffect(() => {
    window.setHpoTerms = setHpoTerms
    window.setDiseases = setDiseases
    window.setSelectedSamples = setSelectedSamples
    window.setSelectedPanels = setSelectedPanels

    return () => {
      delete window.setHpoTerms
      delete window.setDiseases
      delete window.setSelectedSamples
      delete window.setSelectedPanels
    }
  }, [])

  const handleLogoClick = () => {
    setShowChat(true)
    setVisibility(true)
    // scroll to the bottom
    setTimeout(() => {
      scrollToBottom()
    }, 100)
  }

  const handleCloseChat = () => {
    setShowChat(false)
    setVisibility(false)
  }

  const handleNewChat = () => {
    clearChatState()
    setShowChat(true)
  }

  const handleHPOButtonClick = () => {
    setShowHPOWidget(!showHPOWidget)
    setShowDiseaseWidget(false)
    setShowSamplesWidget(false)
    setShowPanelsWidget(false)
  }

  const handleDiseaseButtonClick = () => {
    setShowDiseaseWidget(!showDiseaseWidget)
    setShowHPOWidget(false)
    setShowSamplesWidget(false)
    setShowPanelsWidget(false)
  }

  const handleSamplesButtonClick = () => {
    setShowSamplesWidget(!showSamplesWidget)
    setShowHPOWidget(false)
    setShowDiseaseWidget(false)
    setShowPanelsWidget(false)
  }

  const handlePanelsButtonClick = () => {
    setShowPanelsWidget(!showPanelsWidget)
    setShowHPOWidget(false)
    setShowDiseaseWidget(false)
    setShowSamplesWidget(false)
  }

  const handleCloseHPOWidget = () => {
    setShowHPOWidget(false)
  }

  const handleCloseDiseaseWidget = () => {
    setShowDiseaseWidget(false)
  }

  const handleCloseSamplesWidget = () => {
    setShowSamplesWidget(false)
  }

  const handleClosePanelsWidget = () => {
    setShowPanelsWidget(false)
  }

  const handleRemoveHPOTerm = (termToRemove) => {
    setHpoTerms((prevTerms) => prevTerms.filter((term) => term.hpo_id !== termToRemove.hpo_id))
  }

  const handleRemoveDisease = (diseaseToRemove) => {
    setDiseases((prevDiseases) => prevDiseases.filter((disease) => disease.key !== diseaseToRemove.key))
  }

  const handleRemoveSample = (sampleToRemove) => {
    setSelectedSamples((prevSamples) =>
      prevSamples.filter((sample) => (sample.value || sample.id) !== (sampleToRemove.value || sampleToRemove.id)),
    )
  }

  const handleRemovePanel = (panelToRemove) => {
    setSelectedPanels((prevPanels) =>
      prevPanels.filter((panel) => (panel.value || panel.id) !== (panelToRemove.value || panelToRemove.id)),
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      <AnimatePresence>
        {showChat && (
          <ChatInterface
            onClose={handleCloseChat}
            onNewChat={handleNewChat}
            onHPOButtonClick={handleHPOButtonClick}
            onDiseaseButtonClick={handleDiseaseButtonClick}
            onSamplesButtonClick={handleSamplesButtonClick}
            onPanelsButtonClick={handlePanelsButtonClick}
            isDarkMode={isDarkMode}
            isVariantsTablePage={isVariantsTablePage}
            suggestedQueries={suggestedQueries}
            selectedHPOTerms={hpoTerms}
            onRemoveHPOTerm={handleRemoveHPOTerm}
            selectedDiseases={diseases}
            onRemoveDisease={handleRemoveDisease}
            selectedSamples={selectedSamples}
            onRemoveSample={handleRemoveSample}
            selectedPanels={selectedPanels}
            onRemovePanel={handleRemovePanel}
          />
        )}

        {showHPOWidget && (
          <FilterWidget
            type="hpo"
            title="HPO Terms"
            onClose={handleCloseHPOWidget}
            isDarkMode={isDarkMode}
            hpoTerms={hpoTerms}
            setHpoTerms={setHpoTerms}
          />
        )}

        {showDiseaseWidget && (
          <FilterWidget
            type="disease"
            title="Diseases"
            onClose={handleCloseDiseaseWidget}
            isDarkMode={isDarkMode}
            phenotypes={diseases}
            setPhenotypes={setDiseases}
          />
        )}

        {showSamplesWidget && (
          <FilterWidget
            type="samples"
            title="Samples"
            onClose={handleCloseSamplesWidget}
            isDarkMode={isDarkMode}
            selectedSamples={selectedSamples}
            setSelectedSamples={setSelectedSamples}
          />
        )}

        {showPanelsWidget && (
          <FilterWidget
            type="panels"
            title="Gene Panels"
            onClose={handleClosePanelsWidget}
            isDarkMode={isDarkMode}
            selectedPanels={selectedPanels}
            setSelectedPanels={setSelectedPanels}
          />
        )}
      </AnimatePresence>
    </>
  )
}
